import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Circle, Building2, Shield, Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";
import { useProfile } from "@/hooks/useProfile";
import { useProfileInterests } from "@/hooks/useInterests";
import { useJobPostDetail } from "@/hooks/useJobPosts";
import { useMyApplications } from "@/hooks/useJobApplications";
import { toast } from "@/hooks/use-toast";

const dadosCompartilhados = [
  { label: "Bio" }, { label: "Interesses" }, { label: "Objetivo" },
  { label: "Experiências" }, { label: "Portfólio" }, { label: "Cursos realizados" }, { label: "Sinais de Carreira" },
];
const dadosNaoCompartilhados = ["E-mail pessoal", "Telefone pessoal", "Endereço/CEP", "Renda imediata", "Disponibilidade detalhada"];

export default function AlunoAplicarVaga() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { profile, isLoading: profileLoading } = useProfile();
  const { interests } = useProfileInterests();
  const { data: vaga, isLoading: vagaLoading } = useJobPostDetail(id);
  const { apply } = useMyApplications();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [consentiu, setConsentiu] = useState(false);

  const isLoading = profileLoading || vagaLoading;

  const gates = {
    bio: !!profile?.bio,
    interesses: interests.length > 0,
    objetivo: !!profile?.objective,
  };
  const gatesComplete = Object.values(gates).every(Boolean);
  const completedCount = Object.values(gates).filter(Boolean).length;

  const handleSubmit = async () => {
    if (!gatesComplete || !consentiu || !id) return;
    setIsSubmitting(true);
    try {
      await apply.mutateAsync({ jobId: id, consentVisibility: true });
      setSubmitted(true);
    } catch (err: any) {
      if (err.message?.includes("duplicate")) {
        toast({ title: "Você já se candidatou a esta vaga" });
        navigate("/aluno/vagas/candidaturas");
      } else {
        toast({ title: "Erro ao candidatar", description: err.message, variant: "destructive" });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="page-container flex items-center justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 py-12">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", duration: 0.6 }}
          className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mb-6">
          <CheckCircle2 className="w-10 h-10 text-success" />
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="text-center max-w-sm">
          <h1 className="font-heading text-2xl font-bold text-foreground mb-2">Candidatura enviada!</h1>
          <p className="text-muted-foreground mb-2">Sua candidatura para "{vaga?.title}" foi registrada.</p>
          <Badge variant="construction" className="mb-8">Em triagem</Badge>
          <div className="space-y-3">
            <Button variant="hero" size="lg" className="w-full" onClick={() => navigate("/aluno/vagas/candidaturas")}>Ver Minhas Candidaturas</Button>
            <Button variant="outline" size="lg" className="w-full" onClick={() => navigate("/aluno/vagas")}>Explorar mais vagas</Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/aluno/vagas/${id}`)}><ArrowLeft className="w-5 h-5" /></Button>
        <span className="text-sm text-muted-foreground">Aplicar para vaga</span>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-card rounded-2xl p-5 shadow-card border border-border mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <Building2 className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="font-heading text-lg font-bold text-foreground">{vaga?.title}</h1>
            <p className="text-sm text-muted-foreground">{(vaga?.partner_orgs as any)?.name}</p>
          </div>
        </div>
      </motion.div>

      <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-6">
        <h2 className="font-heading text-base font-semibold text-foreground mb-3">Requisitos para aplicar</h2>
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          {Object.entries(gates).map(([key, value], i) => {
            const labels: Record<string, string> = { bio: "Bio preenchida", interesses: "Interesses definidos", objetivo: "Objetivo atual definido" };
            return (
              <div key={key} className={`flex items-center gap-3 p-4 ${i < Object.keys(gates).length - 1 ? "border-b border-border" : ""}`}>
                {value ? <CheckCircle2 className="w-5 h-5 text-success" /> : <Circle className="w-5 h-5 text-muted-foreground" />}
                <span className={`text-sm flex-1 ${value ? "text-foreground" : "text-muted-foreground"}`}>{labels[key]}</span>
                {!value && <Button size="sm" variant="ghost" onClick={() => navigate("/aluno/perfil/editar")}>Completar</Button>}
              </div>
            );
          })}
        </div>
        {!gatesComplete && (
          <div className="bg-accent/10 rounded-xl p-4 mt-4 border border-accent/20">
            <p className="text-sm font-medium text-foreground">Falta só isso! ({completedCount}/3)</p>
          </div>
        )}
      </motion.section>

      <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Shield className="w-5 h-5 text-primary" />
          <h2 className="font-heading text-base font-semibold text-foreground">O que será compartilhado</h2>
        </div>
        <div className="bg-card rounded-2xl p-4 border border-border mb-4">
          <p className="text-xs text-muted-foreground mb-3">Ao aplicar, este parceiro poderá ver:</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {dadosCompartilhados.map((dado, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-xs bg-success/10 text-success px-2 py-1 rounded-full">
                <Eye className="w-3 h-3" />{dado.label}
              </span>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mb-2"><strong>NÃO</strong> será compartilhado:</p>
          <div className="flex flex-wrap gap-2">
            {dadosNaoCompartilhados.map((dado, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
                <EyeOff className="w-3 h-3" />{dado}
              </span>
            ))}
          </div>
        </div>
        <label className="flex items-start gap-3 p-4 bg-card rounded-xl border border-border cursor-pointer">
          <input type="checkbox" checked={consentiu} onChange={(e) => setConsentiu(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-input" />
          <span className="text-sm text-foreground">Li e concordo que meu perfil será compartilhado com a empresa para esta vaga específica</span>
        </label>
      </motion.section>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="sticky bottom-20 bg-background pt-4">
        <Button variant="hero" size="xl" className="w-full" onClick={handleSubmit} disabled={!gatesComplete || !consentiu || isSubmitting}>
          {isSubmitting ? (<><Loader2 className="w-5 h-5 animate-spin" />Enviando...</>) : (<>Confirmar Candidatura<ArrowRight className="w-5 h-5" /></>)}
        </Button>
      </motion.div>
    </div>
  );
}
